import { useState, useEffect, FC, forwardRef, useImperativeHandle, useRef } from "react";
import { Select } from "antd";

import CustomOption from "./CustomOption";
import CreateCustom from './CreateCustom';

import { Wrapper, NotFound } from "./styled";

interface IProps {
  type: "area" | "category";
  text: string;
  options: any;
  error: string;
  value: any;
  isWidth?: boolean;
  disabled?: boolean;
  dropdownPosition?: "default" | "header";
  onSelect: (value: any) => void;
  onCreate: (value: string, callback: (success: any) => void) => void;
  onDelete: (value: string) => void;
  onDropdownVisibleChange: (visible: boolean) => void;
}

const modifyOptions = (options: any[], value: any, onDelete: (id: string) => void): any[] => {
  return options.map((option) => {
    if ((option as any).custom) {
      return {
        value: option.value,
        label: (
          <CustomOption
            value={option.value}
            label={option.label}
            disabled={option.value === value?.value}
            onDelete={onDelete}
          />
        ),
        originalLabel: option.label,
      };
    }
    return option;
  });
};

const modifyGroupedAreas = (groupedAreas: any[] | any[], value: any, onDelete: (id: string) => void): any[] | any[] => {
  if (Array.isArray(groupedAreas) && groupedAreas.length > 0) {
    if ('options' in groupedAreas[0]) {
      // Grouped areas case
      return (groupedAreas as any[]).map((group) => ({
        ...group,
        options: modifyOptions(group.options, value, onDelete),
      }));
    } else {
      // Flat list of options case
      return modifyOptions(groupedAreas as any[], value, onDelete);
    }
  }
  return groupedAreas;
};

export const getOptionWithLabel = (value: string, options: any[], customOptions: any[]) => {
  const allOptions = [...options, ...customOptions];
  const option = allOptions.find(i => i.id === value) || allOptions.find(i => i.name === value);

  if (!option && value) {
    return {
      value,
      label: value
    };
  }

  if (!option) {
    return undefined;
  }

  return {
    value: option?.id,
    label: option?.name
  };
}

const CustomSelect: FC<IProps> = forwardRef(({ type, text, options, error, disabled, dropdownPosition = "default", onSelect, onCreate, onDelete, onDropdownVisibleChange, value, isWidth, ...rest }, ref: any) => {
  const [searchValue, setSearchValue] = useState("");
  const [open, setOpen] = useState(false);
  const [dropdownHeight, setDropdownHeight] = useState(200);

  const wrapperRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    // Set a default initial height
    updateDropdownHeight();
  }, []);

  // Function to get the parent element at a specific level
  const getParentElement = (element: HTMLElement | null, levels: number): HTMLElement | null => {
    let currentElement = element;

    for (let i = 0; i < levels; i++) {
      if (!currentElement) return null;
      currentElement = currentElement.parentElement;
    }

    return currentElement;
  };

  // Update height when dropdown visibility changes
  const updateDropdownHeight = () => {
    if (wrapperRef.current) {
      // Get the parent element (which would be parentNode.parentNode in the context of the Select)
      const parentElement = getParentElement(wrapperRef.current, 4);
      
      if (parentElement) {
        // Calculate half of the parent element's height
        const parentHeight = parentElement.clientHeight;
        const dynamicHeight = Math.floor(parentHeight / 2) - 100;
        
        // Set a minimum height to ensure usability
        const finalHeight = Math.max(dynamicHeight, 100);
        
        // Update the dropdown height
        setDropdownHeight(finalHeight);
      }
    }
  };

  useImperativeHandle(ref, () => ({
    open: () => setOpen(true),
    close: () => setOpen(false),
  }));

  const handleSearch = (value: string) => {
    setSearchValue(value);
  }

  const handleCreate = (value: string, callback: (success: any) => void) => {
    onCreate(value, callback);
    setSearchValue("");
    onDropdownVisibleChange(false);
  }

  const handleSelect = (newValue: any) => {
    onSelect({ ...newValue, label: newValue?.label?.props?.label ?? newValue?.label });
    setSearchValue("");
    setOpen(false);
  }

  const dropdownVisibleChange = (visible: boolean) => {
    setOpen(visible);
    onDropdownVisibleChange(visible);
    
    // Update dropdown height when dropdown becomes visible
    if (visible) {
      updateDropdownHeight();
    }
  }

  return (
    <Wrapper $error={!!error} $dropdownPosition={dropdownPosition} $disabled={!!disabled} ref={wrapperRef}>
      <Select
        placement="bottomRight"
        open={open}
        {...rest}
        getPopupContainer={(trigger) => trigger.parentNode.parentNode}
        value={value}
        showSearch
        searchValue={searchValue}
        onSelect={handleSelect}
        style={{
          minWidth: "100%",
        }}
        optionFilterProp="value"
        placeholder="Select"
        autoClearSearchValue={false}
        filterOption={(input, option) =>
          ((typeof option?.label === 'string' ? option.label : option?.originalLabel).toLowerCase() ?? "").includes(input.toLowerCase())
        }
        notFoundContent={type === 'category'
          ? <NotFound>
              <span>You don’t have any categories in this area</span>
            </NotFound>
          : null
        }
        labelInValue
        listHeight={dropdownHeight}
        dropdownRender={(menu) => {
          return (
            <>
              {menu}
              <CreateCustom
                type={type}
                onCreate={handleCreate}
                onSelect={handleSelect}
              />
            </>
          )
        }}
        options={modifyGroupedAreas(options, value, onDelete)}
        disabled={disabled}
        onSearch={handleSearch}
        onDropdownVisibleChange={dropdownVisibleChange}
      />
    </Wrapper>
  );
});

export default CustomSelect;
